import { endpoints, oids } from "../../../../configs/Settings";
import { HTTPResponseStatus } from "../../../../utils/enum";

import { useAuth } from "../../../../utils/useAuth";
import { useAxios } from "../../../../utils/useAxios";
import { auxiliarData } from "../../AuxiliarData";
import { ITaskData } from "@/utils/interfaces";
import { PostTask } from "../../task/PostTask";
import { PutServiceRequest } from "./PutServiceRequest";

/** Monta o resource FHIR reutilizando o builder do Bundle (shape idêntico ao POST). */
function buildServiceResource(
  el: any,
  encounter: any,
  contained: any
): any {
  const bundle = JSON.parse(
    auxiliarData.ServiceRequest([el], encounter, contained)
  );
  return {
    ...bundle?.entry?.[0]?.resource,
    id: el.serviceRequestId,
  };
}

/**
 * Persistência de ServiceRequest:
 * - itens novos (sem id) → POST Bundle transaction (como sempre)
 * - itens editados (com serviceRequestId) → PUT individual em paralelo
 * Mantém requisition do lote (hidratada) para não quebrar o agrupamento da solicitação.
 */
export async function PostServiceRequest(
  objData: any,
  encounter: any,
  contained: any
) {
  const list = Array.isArray(objData) ? objData : [];

  // Reusa requisition do lote (edição); itens novos no mesmo lote herdam o grupo
  const sharedRequisition = list.find(
    (el: any) => el?.requisition?.value
  )?.requisition;

  const items = list.map((el: any) => ({
    ...el,
    ...(sharedRequisition && !el?.requisition
      ? { requisition: sharedRequisition }
      : {}),
  }));

  const toCreate = items.filter((el: any) => !el?.serviceRequestId);
  const toUpdate = items.filter((el: any) => el?.serviceRequestId);

  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
  };

  // POST Bundle (só criação) + PUTs individuais em paralelo — sem cascata
  const [createResponse] = await Promise.all([
    toCreate.length
      ? useAxios
          .post(
            `${endpoints.EHRPOSTER}/`,
            auxiliarData.ServiceRequest(toCreate, encounter, contained),
            { headers, timeout: 20000 }
          )
          .then((response) => {
            if (
              response.status !== HTTPResponseStatus.Created &&
              response.status !== HTTPResponseStatus.Success
            ) {
              throw new Error(`PostServiceRequest: ${response.status}`);
            }
            return response.data;
          })
          .catch((error) => {
            console.error("PostServiceRequest ", error);
            throw new Error();
          })
      : Promise.resolve(null),
    toUpdate.length
      ? Promise.all(
          toUpdate.map(async (el: any) => {
            const resource = buildServiceResource(el, encounter, contained);
            await PutServiceRequest(resource, el.serviceRequestId);
            return el.serviceRequestId as string;
          })
        )
      : Promise.resolve([]),
  ]);

  // Task só em solicitação nova (há itens sem id no lote)
  if (toCreate.length && encounter?.requester) {
    const taskData: ITaskData = {
      status: "requested",
      intent: "order",
      priority: "routine",
      description: `O médico ${encounter.requester.display} solicitou ${
        toCreate?.length > 1 ? "os exames" : "o exame"
      } ${toCreate?.map((item: any) => item.name)?.join(", ")}`,

      owner: encounter?.requester?.reference,
      for: `Patient/${oids.cpf}-${encounter?.subject}`,
    };

    PostTask(taskData);
  }

  // Resposta Bundle-like alinhada à ordem de `items` (Footer/basedOn)
  let createEntryCursor = 0;
  const entry = items.map((el: any) => {
    if (el?.serviceRequestId) {
      return {
        response: {
          status: "200 OK",
          location: `ServiceRequest/${el.serviceRequestId}`,
        },
      };
    }
    const created = createResponse?.entry?.[createEntryCursor++];
    return (
      created ?? {
        response: { status: "201 Created" },
      }
    );
  });

  return {
    resourceType: "Bundle",
    type: "transaction-response",
    entry,
  };
}
